import React, { useState, useCallback, useEffect } from 'react';
import MinimalInput from '../../components/MinimalInput/MinimalInput';
import { motion, AnimatePresence } from 'framer-motion';
import { loginClient, loginLawyer } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../components/CustomButton/CustomButton';
import AuthNavBar from '../../components/layout/AuthNavBar';
import { useAuthMode } from '../../redux/hooks';

function Login() {
  const navigate = useNavigate();
  const { isLawyer, setClientMode, setLawyerMode } = useAuthMode();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [logError, setLogError] = useState(false);
  const [textError, setTextError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = useCallback(
    field => e => {
      setFormData(prev => ({
        ...prev,
        [field]: e.target.value,
      }));
      setLogError(false);
    },
    []
  );

  const onLogin = useCallback(
    async e => {
      e.preventDefault();
      if (loading) return;

      setLoading(true);
      setLogError(false);
      setTextError('');

      try {
        const response = isLawyer
          ? await loginLawyer(formData.username, formData.password)
          : await loginClient(formData.username, formData.password);

        if (response.status === 0 || response.status === -3) {
          localStorage.setItem(
            'user',
            JSON.stringify({
              id: response.id,
              name: response.name,
              type: isLawyer ? 'lawyer' : 'client',
              logg_express: response.logg_express,
            })
          );

          navigate(isLawyer ? '/lawyer-dashboard' : '/client-dashboard');
        } else {
          setTextError(response.message || 'Error en el login');
          setLogError(true);
        }
      } catch (error) {
        setTextError(error.message || 'Error en el login');
        setLogError(true);
      } finally {
        setLoading(false);
      }
    },
    [formData.username, formData.password, isLawyer, loading, navigate]
  );

  const handleUserType = useCallback(
    type => {
      if (type === 'lawyer') {
        setLawyerMode();
      } else {
        setClientMode();
      }
      setLogError(false);
      setTextError('');
    },
    [setLawyerMode, setClientMode]
  );

  const toggleShowPassword = () => setShowPassword(v => !v);

  return (
    <>
      <AuthNavBar />
      <motion.div
        className="flex flex-col items-center justify-start px-4"
        initial={false}
        animate={{ backgroundColor: isLawyer ? '#132A56' : '#fbfcf8' }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        style={{ minHeight: 'calc(100vh - 80px)' }}
      >
        <div className="w-full max-w-sm mx-auto flex flex-col gap-6 mt-2">
          <div className="text-center mb-2">
            <h1
              className={`text-5xl font-serif italic font-bold mb-1 ${
                isLawyer ? 'text-white' : 'text-custom-blue'
              }`}
            >
              ¡Bienvenido!
            </h1>
            <h2
              className={`text-3xl font-light mb-6 ${
                isLawyer ? 'text-white' : 'text-gray-900'
              } leading-10 mt-4`}
              style={{ minHeight: '2.5rem' }}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={isLawyer ? 'lawyer' : 'client'}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="inline-block"
                >
                  {isLawyer
                    ? 'Inicia sesión, es hora de defender la justicia'
                    : 'Inicia sesión, tus problemas están resueltos'}
                </motion.span>
              </AnimatePresence>
            </h2>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div
              className={`flex relative ${
                isLawyer ? 'border-b border-gray-500' : 'border-b border-gray-300'
              }`}
            >
              <button
                type="button"
                className={`px-8 py-3 text-lg font-medium transition-colors duration-200 relative ${
                  !isLawyer
                    ? isLawyer
                      ? 'text-white'
                      : 'text-black'
                    : isLawyer
                      ? 'text-gray-400'
                      : 'text-gray-500'
                }`}
                onClick={() => handleUserType('client')}
              >
                Soy Cliente
                {!isLawyer && (
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-200 ${
                      isLawyer ? 'bg-white' : 'bg-black'
                    }`}
                  />
                )}
              </button>
              <button
                type="button"
                className={`px-8 py-3 text-lg font-medium transition-colors duration-200 relative ${
                  isLawyer
                    ? isLawyer
                      ? 'text-white'
                      : 'text-black'
                    : isLawyer
                      ? 'text-gray-400'
                      : 'text-gray-500'
                }`}
                onClick={() => handleUserType('lawyer')}
              >
                Soy Abogado
                {isLawyer && (
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-200 ${
                      isLawyer ? 'bg-white' : 'bg-black'
                    }`}
                  />
                )}
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={isLawyer ? 'lawyer-login' : 'client-login'}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <form onSubmit={onLogin} className="flex flex-col gap-3">
                <MinimalInput
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange('username')}
                  placeholder="Correo"
                  autoComplete="username"
                  className={`rounded-lg px-4 py-3 text-base transition-colors duration-200 ${
                    isLawyer
                      ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
                      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
                  } border`}
                />
                <div className="relative">
                  <MinimalInput
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    placeholder="Contraseña"
                    autoComplete="current-password"
                    className={`rounded-lg px-4 py-3 text-base pr-10 transition-colors duration-200 ${
                      isLawyer
                        ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
                        : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
                    } border`}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={toggleShowPassword}
                    className={`absolute right-3 top-6 transform -translate-y-1/2 focus:outline-none transition-colors duration-200 flex items-center justify-center ${
                      isLawyer ? 'text-gray-300' : 'text-gray-500'
                    }`}
                  >
                    {showPassword ? (
                      <svg
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                      >
                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                      >
                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a21.77 21.77 0 0 1 5.06-6.06M1 1l22 22" />
                        <path d="M9.53 9.53A3.001 3.001 0 0 0 12 15a3 3 0 0 0 2.47-5.47" />
                      </svg>
                    )}
                  </button>
                </div>
                {logError && (
                  <div className="text-red-500 text-xs mb-2 text-center">{textError}</div>
                )}
                <div className="flex justify-center mb-7 -mt-4">
                  <a
                    href="#"
                    className={`text-xs font-serif underline transition-colors ${
                      isLawyer
                        ? 'text-blue-300 hover:text-blue-100'
                        : 'text-blue-700 hover:text-blue-900'
                    }`}
                  >
                    ¿Olvidaste tu contraseña?
                  </a>
                </div>
                <CustomButton
                  type="submit"
                  text={loading ? 'Entrando...' : 'Inicia Sesión'}
                  disabled={loading}
                  className="w-full justify-center py-3 text-base"
                />
              </form>
              <div
                className={`text-center mt-2 text-sm ${isLawyer ? 'text-gray-200' : 'text-gray-700'}`}
              >
                ¿Aún no tienes una cuenta?{' '}
                <a
                  href="/signup"
                  className={`underline transition-colors ${
                    isLawyer
                      ? 'text-blue-300 hover:text-blue-100'
                      : 'text-blue-700 hover:text-blue-900'
                  }`}
                >
                  Entra Aquí
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
}

export default Login;
